import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Mountain, Gem, RefreshCw, Play, Settings, Search,
  Loader2, CheckCircle, XCircle, Clock, AlertTriangle, Activity, Zap
} from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import ModuleHeader from "@/components/ModuleHeader";

function formatNumber(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}

function formatDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function formatDuration(s: number | null | undefined) {
  if (!s) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function formatEta(elapsedSec: number, progress: number, total: number): string {
  if (!progress || !total || progress >= total) return "—";
  const rate = progress / elapsedSec;
  const remaining = (total - progress) / rate;
  if (!isFinite(remaining)) return "—";
  const m = Math.floor(remaining / 60);
  const s = Math.round(remaining % 60);
  if (m > 60) return `~${Math.round(m / 60)}h`;
  return m > 0 ? `~${m}m ${s}s` : `~${s}s`;
}

interface LogEntry {
  ts: number;
  msg: string;
  type: "info" | "done" | "error";
}

export default function CprmPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [latGeo, setLatGeo] = useState("-15.7801");
  const [lonGeo, setLonGeo] = useState("-47.9292");
  const [latRec, setLatRec] = useState("-15.7801");
  const [lonRec, setLonRec] = useState("-47.9292");
  const [raio, setRaio] = useState("50");
  const [substancia, setSubstancia] = useState("");
  const [uf, setUf] = useState("");
  const [geoResult, setGeoResult] = useState<any>(null);
  const [recResult, setRecResult] = useState<any>(null);
  const [queryLoading, setQueryLoading] = useState(false);

  const [cfgEnabled, setCfgEnabled] = useState<boolean | null>(null);
  const [cfgDays, setCfgDays] = useState<string>("");

  // Lista Geologia
  const [geoListSigla, setGeoListSigla] = useState("");
  const [geoListHierarquia, setGeoListHierarquia] = useState("");
  const [geoListBusca, setGeoListBusca] = useState("");
  const [geoListPage, setGeoListPage] = useState(0);
  const GEO_LIMIT = 50;

  // Lista Recursos
  const [recListSubst, setRecListSubst] = useState("");
  const [recListUf, setRecListUf] = useState("");
  const [recListStatus, setRecListStatus] = useState("");
  const [recListBusca, setRecListBusca] = useState("");
  const [recListPage, setRecListPage] = useState(0);
  const REC_LIMIT = 50;

  // live log accumulation
  const [liveLogs, setLiveLogs] = useState<LogEntry[]>([]);
  const lastLogRef = useRef<string>("");
  const syncStartRef = useRef<number | null>(null);
  const liveLogEndRef = useRef<HTMLDivElement>(null);
  const prevSyncingRef = useRef(false);

  const { data: status, isFetching: statusFetching } = useQuery({
    queryKey: ["/api/cprm/status"],
    queryFn: () => authFetch("/api/cprm/status"),
    refetchInterval: (query) => {
      const d = query.state.data as any;
      return d?.sync_ativo ? 1000 : 10000;
    },
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["/api/cprm/config"],
    queryFn: () => authFetch("/api/cprm/config"),
  });

  const { data: syncLog } = useQuery({
    queryKey: ["/api/cprm/sync-log"],
    queryFn: () => authFetch("/api/cprm/sync-log"),
    refetchInterval: status?.sync_ativo ? 5000 : 30000,
  });

  const { data: geoListData, isLoading: geoListLoading } = useQuery({
    queryKey: ["/api/cprm/geologia/lista", geoListSigla, geoListHierarquia, geoListBusca, geoListPage],
    queryFn: () =>
      authFetch(
        `/api/cprm/geologia/lista?sigla=${encodeURIComponent(geoListSigla)}&hierarquia=${encodeURIComponent(geoListHierarquia)}&busca=${encodeURIComponent(geoListBusca)}&limit=${GEO_LIMIT}&offset=${geoListPage * GEO_LIMIT}`
      ),
  });

  const { data: recListData, isLoading: recListLoading } = useQuery({
    queryKey: ["/api/cprm/recursos/lista", recListSubst, recListUf, recListStatus, recListBusca, recListPage],
    queryFn: () =>
      authFetch(
        `/api/cprm/recursos/lista?substancia=${encodeURIComponent(recListSubst)}&uf=${recListUf}&status=${encodeURIComponent(recListStatus)}&busca=${encodeURIComponent(recListBusca)}&limit=${REC_LIMIT}&offset=${recListPage * REC_LIMIT}`
      ),
  });

  // accumulate live log entries
  useEffect(() => {
    const syncing = status?.sync_ativo;
    const log = status?.sync_log;
    const err = status?.sync_erro;

    if (syncing && !prevSyncingRef.current) {
      syncStartRef.current = Date.now();
      setLiveLogs([{ ts: Date.now(), msg: "Importação iniciada...", type: "info" }]);
      lastLogRef.current = "";
    }

    if (!syncing && prevSyncingRef.current) {
      const finalMsg = err
        ? `Erro: ${err}`
        : (log || "Sincronização concluída");
      setLiveLogs(prev => [...prev, { ts: Date.now(), msg: finalMsg, type: err ? "error" : "done" }]);
      queryClient.invalidateQueries({ queryKey: ["/api/cprm/sync-log"] });
    }

    if (syncing && log && log !== lastLogRef.current) {
      lastLogRef.current = log;
      setLiveLogs(prev => [...prev, { ts: Date.now(), msg: log, type: "info" }]);
    }

    prevSyncingRef.current = !!syncing;
  }, [status?.sync_ativo, status?.sync_log, status?.sync_erro]);

  // auto-scroll live log
  useEffect(() => {
    liveLogEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [liveLogs]);

  const syncMutation = useMutation({
    mutationFn: (tipo?: string) =>
      authFetch("/api/cprm/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tipo ? { tipo } : {}),
      }),
    onSuccess: (data) => {
      toast({ title: "Sync iniciado", description: data.message });
      queryClient.invalidateQueries({ queryKey: ["/api/cprm/status"] });
    },
    onError: () => toast({ title: "Erro", description: "Falha ao iniciar sync", variant: "destructive" }),
  });

  const configMutation = useMutation({
    mutationFn: (body: object) =>
      authFetch("/api/cprm/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast({ title: "Configuração salva" });
      queryClient.invalidateQueries({ queryKey: ["/api/cprm/config"] });
      queryClient.invalidateQueries({ queryKey: ["/api/cprm/status"] });
    },
    onError: () => toast({ title: "Erro", description: "Falha ao salvar configuração", variant: "destructive" }),
  });

  async function queryGeologia() {
    if (!latGeo || !lonGeo) return;
    setQueryLoading(true);
    setGeoResult(null);
    try {
      const res = await authFetch(`/api/cprm/geologia?lat=${latGeo}&lon=${lonGeo}`);
      const data = await res.json();
      setGeoResult(Array.isArray(data) ? { data } : data);
    } catch {
      setGeoResult({ error: "Erro ao consultar" });
    } finally {
      setQueryLoading(false);
    }
  }

  async function queryRecursos() {
    setQueryLoading(true);
    setRecResult(null);
    try {
      let url: string;
      if (substancia || uf) {
        url = `/api/cprm/recursos/substancia?substancia=${substancia}&uf=${uf}&limit=50`;
      } else {
        url = `/api/cprm/recursos?lat=${latRec}&lon=${lonRec}&raio=${raio}&limit=50`;
      }
      const res = await authFetch(url);
      const data = await res.json();
      setRecResult(data);
    } catch {
      setRecResult({ error: "Erro ao consultar" });
    } finally {
      setQueryLoading(false);
    }
  }

  const geoTotal = status?.tabelas?.cprm_geologia?.total ?? 0;
  const recTotal = status?.tabelas?.cprm_recursos_minerais?.total ?? 0;
  const syncing = status?.sync_ativo ?? false;
  const syncProgress = status?.sync_progresso ?? 0;
  const syncTotalProg = status?.sync_total ?? 0;
  const pct = syncTotalProg > 0 ? Math.min(100, Math.round((syncProgress / syncTotalProg) * 100)) : 0;
  const elapsedSec = syncStartRef.current ? (Date.now() - syncStartRef.current) / 1000 : 0;
  const eta = syncing ? formatEta(elapsedSec, syncProgress, syncTotalProg) : "—";
  const rate = elapsedSec > 1 ? Math.round(syncProgress / elapsedSec) : 0;

  const effectiveEnabled = cfgEnabled !== null ? cfgEnabled : (config?.enabled ?? true);
  const effectiveDays = cfgDays !== "" ? cfgDays : String(config?.intervalDays ?? 90);

  return (
    <div className="space-y-6" data-testid="page-cprm">

      <ModuleHeader
        icon={Mountain}
        iconColor="text-primary"
        title="CPRM / SGB — Geologia"
        description={`Mapa Litoestratigráfico 1:2.500.000 · ${formatNumber(geoTotal)} unidades · ${formatNumber(recTotal)} ocorrências minerais`}
        recordCount={geoTotal + recTotal}
        recordLabel="registros"
        lastUpdate={status?.ultima_sync_global}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ["/api/cprm/status"] })}
        refreshing={statusFetching}
        docsHref="/docs"
        extra={
          <Button
            size="sm"
            onClick={() => syncMutation.mutate(undefined)}
            disabled={syncing || syncMutation.isPending}
            data-testid="button-cprm-sync-full"
          >
            {syncing || syncMutation.isPending
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Play className="h-4 w-4 mr-1" />}
            {syncing ? "Sincronizando..." : "Sync Completo"}
          </Button>
        }
      />

      {/* Live sync monitor — shown when active */}
      {(syncing || liveLogs.length > 0) && (
        <Card className={`border-2 ${syncing ? "border-blue-500/60 bg-blue-500/5" : "border-muted"}`} data-testid="card-cprm-live-monitor">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className={`h-4 w-4 ${syncing ? "text-blue-500 animate-pulse" : "text-muted-foreground"}`} />
              Monitor em Tempo Real
              {syncing && (
                <span className="flex items-center gap-1 text-xs font-normal text-blue-500 ml-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                  AO VIVO
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {syncing && (
              <>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>
                    <Badge variant="outline" className="text-blue-500 border-blue-400 mr-2 capitalize">
                      {status?.sync_tipo === "geologia" ? "Geologia" : status?.sync_tipo === "recursos" ? "Recursos" : "Sync"}
                    </Badge>
                    {formatNumber(syncProgress)} / {formatNumber(syncTotalProg)} registros
                  </span>
                  <div className="flex items-center gap-4">
                    {rate > 0 && (
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-yellow-500" />
                        {formatNumber(rate)} reg/s
                      </span>
                    )}
                    {eta !== "—" && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        ETA {eta}
                      </span>
                    )}
                    <span className="font-bold text-primary">{pct}%</span>
                  </div>
                </div>
                <Progress value={pct || (syncTotalProg === 0 ? 15 : 0)} className="h-2" />
              </>
            )}

            {/* Live log feed */}
            <div className="bg-black/80 rounded-md p-3 h-40 overflow-y-auto font-mono text-xs space-y-0.5" data-testid="div-cprm-live-log">
              {liveLogs.map((entry, i) => (
                <div key={i} className={`flex gap-2 leading-5 ${entry.type === "done" ? "text-green-400" : entry.type === "error" ? "text-red-400" : "text-blue-300"}`}>
                  <span className="text-muted-foreground shrink-0">
                    {new Date(entry.ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </span>
                  <span>{entry.type === "done" ? "✓ " : entry.type === "error" ? "✗ " : "→ "}{entry.msg}</span>
                </div>
              ))}
              {liveLogs.length === 0 && (
                <div className="text-muted-foreground">Aguardando inicio de sincronização...</div>
              )}
              <div ref={liveLogEndRef} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card data-testid="card-cprm-geo-total">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Mountain className="h-4 w-4" /> Unidades Geológicas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{formatNumber(geoTotal)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Última sync: {formatDate(status?.tabelas?.cprm_geologia?.ultima_sync)}
            </div>
            {status?.tabelas?.cprm_geologia?.erro && !status?.tabelas?.cprm_geologia?.erro?.includes(" erros") && (
              <div className="text-xs text-destructive mt-1 truncate">{status.tabelas.cprm_geologia.erro}</div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-cprm-rec-total">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Gem className="h-4 w-4" /> Ocorrências Minerais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{formatNumber(recTotal)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Última sync: {formatDate(status?.tabelas?.cprm_recursos_minerais?.ultima_sync)}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-cprm-last-sync">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" /> Próxima Atualização
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-semibold">{formatDate(status?.proxima_sync)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Intervalo: {config?.intervalDays ?? "—"} dias
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-cprm-sync-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <RefreshCw className="h-4 w-4" /> Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {syncing ? (
              <>
                <Badge className="bg-blue-500 text-white mb-1 text-xs">
                  {status?.sync_tipo === "geologia" ? "Importando Geologia" : status?.sync_tipo === "recursos" ? "Importando Recursos" : "Sincronizando"}
                </Badge>
                <div className="text-xs text-muted-foreground mt-1">{pct}% · {formatNumber(syncProgress)}/{formatNumber(syncTotalProg)}</div>
              </>
            ) : (
              <>
                <Badge variant="outline" className="text-green-500 border-green-500 text-xs">Pronto</Badge>
                <div className="text-xs text-muted-foreground mt-1">
                  Geo: {formatNumber(geoTotal)} · Rec: {formatNumber(recTotal)}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="lista-geo" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="lista-geo" data-testid="tab-cprm-lista-geo">Unidades Geológicas</TabsTrigger>
          <TabsTrigger value="lista-rec" data-testid="tab-cprm-lista-rec">Ocorrências Minerais</TabsTrigger>
          <TabsTrigger value="consulta-geo" data-testid="tab-cprm-geo">Geologia por Ponto</TabsTrigger>
          <TabsTrigger value="consulta-rec" data-testid="tab-cprm-rec">Consulta Raio</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-cprm-log">Log</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-cprm-config">Config</TabsTrigger>
        </TabsList>

        {/* TAB: Lista Unidades Geológicas */}
        <TabsContent value="lista-geo">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Mountain className="h-4 w-4 text-primary" />
                Unidades Geológicas — {formatNumber(geoListData?.total)} registros
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Sigla</Label>
                  <Input placeholder="ex: PPlmtm" value={geoListSigla} onChange={e => { setGeoListSigla(e.target.value); setGeoListPage(0); }} data-testid="input-geo-lista-sigla" />
                </div>
                <div className="space-y-1">
                  <Label>Hierarquia</Label>
                  <Input placeholder="ex: Formação, Grupo..." value={geoListHierarquia} onChange={e => { setGeoListHierarquia(e.target.value); setGeoListPage(0); }} data-testid="input-geo-lista-hierarquia" />
                </div>
                <div className="space-y-1">
                  <Label>Busca livre</Label>
                  <Input placeholder="nome, litotipo..." value={geoListBusca} onChange={e => { setGeoListBusca(e.target.value); setGeoListPage(0); }} data-testid="input-geo-lista-busca" />
                </div>
              </div>

              {geoListLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="table-geo-lista">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-3 font-medium">Sigla</th>
                        <th className="pb-2 pr-3 font-medium">Nome</th>
                        <th className="pb-2 pr-3 font-medium">Hierarquia</th>
                        <th className="pb-2 pr-3 font-medium">Eon</th>
                        <th className="pb-2 font-medium">Ambiente Tectônico</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoListData?.data?.map((g: any) => (
                        <tr key={g.id} className="border-b hover:bg-muted/40 transition-colors" data-testid={`row-geo-${g.id}`}>
                          <td className="py-1.5 pr-3 font-mono text-xs font-bold text-primary">{g.sigla || "—"}</td>
                          <td className="py-1.5 pr-3 max-w-[220px] truncate" title={g.nome}>{g.nome || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs text-muted-foreground">{g.hierarquia || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs whitespace-nowrap">
                            {[g.eon_min, g.eon_max].filter(Boolean).join(" – ") || "—"}
                          </td>
                          <td className="py-1.5 text-xs text-muted-foreground truncate max-w-[180px]" title={g.ambiente_tectonico}>{g.ambiente_tectonico || "—"}</td>
                        </tr>
                      ))}
                      {geoListData?.data?.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Nenhuma unidade encontrada</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button variant="outline" size="sm" disabled={geoListPage === 0} onClick={() => setGeoListPage(p => p - 1)} data-testid="button-geo-prev">Anterior</Button>
                <span className="text-sm text-muted-foreground">Pág {geoListPage + 1} · {formatNumber(geoListData?.total)} unidades</span>
                <Button variant="outline" size="sm" disabled={!geoListData || (geoListPage + 1) * GEO_LIMIT >= (geoListData?.total ?? 0)} onClick={() => setGeoListPage(p => p + 1)} data-testid="button-geo-next">Próxima</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Lista Ocorrências Minerais */}
        <TabsContent value="lista-rec">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Gem className="h-4 w-4 text-yellow-500" />
                Ocorrências Minerais — {formatNumber(recListData?.total)} registros
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label>Substância</Label>
                  <Input placeholder="ex: OURO, COBRE..." value={recListSubst} onChange={e => { setRecListSubst(e.target.value); setRecListPage(0); }} data-testid="input-rec-lista-subst" />
                </div>
                <div className="space-y-1">
                  <Label>UF</Label>
                  <Input placeholder="ex: PA, MG..." value={recListUf} maxLength={2} onChange={e => { setRecListUf(e.target.value.toUpperCase()); setRecListPage(0); }} data-testid="input-rec-lista-uf" />
                </div>
                <div className="space-y-1">
                  <Label>Status Econômico</Label>
                  <Input placeholder="Garimpo, Mina..." value={recListStatus} onChange={e => { setRecListStatus(e.target.value); setRecListPage(0); }} data-testid="input-rec-lista-status" />
                </div>
                <div className="space-y-1">
                  <Label>Busca livre</Label>
                  <Input placeholder="nome, município..." value={recListBusca} onChange={e => { setRecListBusca(e.target.value); setRecListPage(0); }} data-testid="input-rec-lista-busca" />
                </div>
              </div>

              {recListLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="table-rec-lista">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-3 font-medium">Ocorrência</th>
                        <th className="pb-2 pr-3 font-medium">Município/UF</th>
                        <th className="pb-2 pr-3 font-medium">Substâncias</th>
                        <th className="pb-2 pr-3 font-medium">Status</th>
                        <th className="pb-2 font-medium">Importância</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recListData?.data?.map((r: any) => (
                        <tr key={r.id} className="border-b hover:bg-muted/40 transition-colors" data-testid={`row-rec-${r.id}`}>
                          <td className="py-1.5 pr-3 max-w-[200px] truncate font-medium" title={r.nome_ocorrencia}>{r.nome_ocorrencia || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs text-muted-foreground whitespace-nowrap">{[r.municipio, r.uf].filter(Boolean).join("/") || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs max-w-[180px] truncate text-yellow-600 dark:text-yellow-400" title={r.substancias}>{r.substancias || "—"}</td>
                          <td className="py-1.5 pr-3 text-xs">{r.status_economico || "—"}</td>
                          <td className="py-1.5 text-xs text-muted-foreground">{r.importancia || "—"}</td>
                        </tr>
                      ))}
                      {recListData?.data?.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Nenhuma ocorrência encontrada</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button variant="outline" size="sm" disabled={recListPage === 0} onClick={() => setRecListPage(p => p - 1)} data-testid="button-rec-prev">Anterior</Button>
                <span className="text-sm text-muted-foreground">Pág {recListPage + 1} · {formatNumber(recListData?.total)} ocorrências</span>
                <Button variant="outline" size="sm" disabled={!recListData || (recListPage + 1) * REC_LIMIT >= (recListData?.total ?? 0)} onClick={() => setRecListPage(p => p + 1)} data-testid="button-rec-next">Próxima</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Geologia */}
        <TabsContent value="consulta-geo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Consulta Geológica por Coordenada</CardTitle>
              <p className="text-sm text-muted-foreground">Retorna a unidade litoestratigráfica no ponto informado (mapa 1:2.500.000)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 flex-wrap">
                <div className="flex-1 min-w-[120px]">
                  <Label>Latitude</Label>
                  <Input value={latGeo} onChange={e => setLatGeo(e.target.value)} placeholder="-15.7801" data-testid="input-cprm-lat-geo" />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <Label>Longitude</Label>
                  <Input value={lonGeo} onChange={e => setLonGeo(e.target.value)} placeholder="-47.9292" data-testid="input-cprm-lon-geo" />
                </div>
                <div className="flex items-end">
                  <Button onClick={queryGeologia} disabled={queryLoading} data-testid="button-cprm-query-geo">
                    {queryLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Consultar
                  </Button>
                </div>
              </div>

              {geoResult && (
                <div className="mt-4" data-testid="result-cprm-geo">
                  {geoResult.error ? (
                    <div className="flex items-center gap-2 text-destructive"><XCircle className="h-4 w-4" />{geoResult.error}</div>
                  ) : !geoResult.data || geoResult.data.length === 0 ? (
                    <div className="flex items-center gap-2 text-muted-foreground"><AlertTriangle className="h-4 w-4" />Nenhuma unidade geológica encontrada neste ponto</div>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-sm text-muted-foreground">{geoResult.data.length} unidade(s) geológica(s)</div>
                      {geoResult.data.map((item: any, i: number) => (
                        <Card key={item.id ?? i} className="bg-muted/30">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-sm">{item.nome || "—"}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{item.hierarquia}</div>
                              </div>
                              <Badge variant="outline" className="shrink-0">{item.sigla}</Badge>
                            </div>
                            <Separator className="my-2" />
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                              <div><span className="text-muted-foreground">Era: </span>{item.era_min || "—"}{item.era_max && item.era_max !== item.era_min ? ` – ${item.era_max}` : ""}</div>
                              <div><span className="text-muted-foreground">Período: </span>{item.sistema_min || "—"}</div>
                              <div><span className="text-muted-foreground">Éon: </span>{item.eon_min || "—"}</div>
                              <div><span className="text-muted-foreground">Ambiente: </span>{item.ambiente_tectonico || "—"}</div>
                              {item.grupo && <div className="col-span-2"><span className="text-muted-foreground">Grupo: </span>{item.grupo}</div>}
                              {item.litotipos && (
                                <div className="col-span-2"><span className="text-muted-foreground">Litotipos: </span>{item.litotipos}</div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Recursos Minerais */}
        <TabsContent value="consulta-rec" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Consulta de Ocorrências Minerais</CardTitle>
              <p className="text-sm text-muted-foreground">Busque por raio geográfico (km) ou por substância e UF</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="text-sm font-medium text-muted-foreground">Por coordenada + raio</div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Latitude</Label>
                      <Input value={latRec} onChange={e => setLatRec(e.target.value)} placeholder="-15.78" data-testid="input-cprm-lat-rec" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Longitude</Label>
                      <Input value={lonRec} onChange={e => setLonRec(e.target.value)} placeholder="-47.92" data-testid="input-cprm-lon-rec" />
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">Raio (km)</Label>
                      <Input value={raio} onChange={e => setRaio(e.target.value)} placeholder="50" data-testid="input-cprm-raio" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-medium text-muted-foreground">Por substância / UF</div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Substância (ex: OURO)</Label>
                      <Input value={substancia} onChange={e => setSubstancia(e.target.value)} placeholder="OURO" data-testid="input-cprm-substancia" />
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">UF</Label>
                      <Input value={uf} onChange={e => setUf(e.target.value.toUpperCase())} placeholder="MG" maxLength={2} data-testid="input-cprm-uf" />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={queryRecursos} disabled={queryLoading} data-testid="button-cprm-query-rec">
                {queryLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Buscar Ocorrências
              </Button>

              {recResult && (
                <div className="mt-4" data-testid="result-cprm-rec">
                  {recResult.error ? (
                    <div className="flex items-center gap-2 text-destructive"><XCircle className="h-4 w-4" />{recResult.error}</div>
                  ) : (
                    <>
                      <div className="text-sm text-muted-foreground mb-3">
                        {formatNumber(recResult.total)} ocorrência(s) encontrada(s)
                        {recResult.filtros?.substancia && <span className="ml-2 text-primary">· {recResult.filtros.substancia}</span>}
                        {recResult.filtros?.uf && <span className="ml-1 text-primary">· {recResult.filtros.uf}</span>}
                      </div>
                      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                        {recResult.data?.map((item: any, i: number) => (
                          <Card key={item.id ?? i} className="bg-muted/30" data-testid={`card-rec-${item.id}`}>
                            <CardContent className="pt-3 pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-medium text-sm">{item.nome_ocorrencia || "Sem nome"}</div>
                                  <div className="text-xs text-muted-foreground">{item.municipio} — {item.uf}</div>
                                </div>
                                <div className="flex gap-1 flex-wrap justify-end">
                                  {item.status_economico && (
                                    <Badge variant="outline" className="text-xs">{item.status_economico}</Badge>
                                  )}
                                  {item.distancia_km != null && (
                                    <Badge className="text-xs bg-primary/20 text-primary border-0">{item.distancia_km} km</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-2">
                                {item.substancias && <div className="col-span-2"><span className="text-muted-foreground">Substâncias: </span>{item.substancias}</div>}
                                {item.provincia && <div><span className="text-muted-foreground">Província: </span>{item.provincia}</div>}
                                {item.importancia && <div><span className="text-muted-foreground">Importância: </span>{item.importancia}</div>}
                                {item.classes_utilitarias && <div className="col-span-2"><span className="text-muted-foreground">Classes: </span>{item.classes_utilitarias}</div>}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {(!recResult.data || recResult.data.length === 0) && (
                          <div className="text-sm text-muted-foreground py-4 text-center">Nenhuma ocorrência encontrada</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Log */}
        <TabsContent value="log">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Log de Sincronizações</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => syncMutation.mutate("geologia")} disabled={syncing} data-testid="button-cprm-sync-geo">
                    <Mountain className="h-3 w-3 mr-1" /> Sync Geologia
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => syncMutation.mutate("recursos")} disabled={syncing} data-testid="button-cprm-sync-rec">
                    <Gem className="h-3 w-3 mr-1" /> Sync Recursos
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="table-cprm-log">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Tipo</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Registros</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Duração</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Status</th>
                      <th className="pb-2 font-medium text-muted-foreground">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncLog?.data?.map((row: any) => (
                      <tr key={row.id} className="border-b border-muted/50 hover:bg-muted/20" data-testid={`row-log-${row.id}`}>
                        <td className="py-2 pr-4">
                          <Badge variant="outline" className="text-xs capitalize">{row.tipo}</Badge>
                        </td>
                        <td className="py-2 pr-4">{formatNumber(row.registros_importados)}</td>
                        <td className="py-2 pr-4">{formatDuration(row.duracao_seg)}</td>
                        <td className="py-2 pr-4">
                          {row.erro ? (
                            <div className="flex items-center gap-1 text-destructive text-xs">
                              <XCircle className="h-3 w-3" /> {row.erro.substring(0, 60)}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-green-500 text-xs">
                              <CheckCircle className="h-3 w-3" /> OK
                            </div>
                          )}
                        </td>
                        <td className="py-2 text-muted-foreground text-xs">{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                    {(!syncLog?.data || syncLog.data.length === 0) && (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-muted-foreground text-sm">
                          Nenhuma sincronização realizada ainda
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Config */}
        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-4 w-4" /> Configuração do Scheduler
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Os dados do CPRM/SGB são atualizados ~1x por ano. Intervalo padrão de 90 dias.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {configLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">Sync automático habilitado</div>
                      <div className="text-xs text-muted-foreground">Re-importa dados automaticamente a cada N dias</div>
                    </div>
                    <Switch
                      checked={effectiveEnabled}
                      onCheckedChange={v => setCfgEnabled(v)}
                      data-testid="switch-cprm-enabled"
                    />
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <Label>Intervalo em dias</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={effectiveDays}
                        onChange={e => setCfgDays(e.target.value)}
                        className="w-32"
                        data-testid="input-cprm-interval-days"
                      />
                      <span className="text-sm text-muted-foreground">dias (mín. 1, máx. 365)</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const body: any = { enabled: effectiveEnabled };
                      const days = parseInt(effectiveDays);
                      if (!isNaN(days) && days >= 1) body.intervalDays = days;
                      configMutation.mutate(body);
                    }}
                    disabled={configMutation.isPending}
                    data-testid="button-cprm-save-config"
                  >
                    {configMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Salvar Configuração
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
